import type { ComponentType, CSSProperties, ReactNode } from 'react';
import cx from 'clsx';
import styles from './widget-grid.module.css';

export type GlobalFilters = Record<string, string>;
export interface WidgetConfig {
  id: string;
  size: WidgetSize;
  widgetComponent: ComponentType<{
    globalFilters?: GlobalFilters;
    sizeClass: string;
    size: WidgetSize;
  }>;
  isHidden?: boolean;
}

export interface WidgetRow {
  id: string;
  widgets: WidgetConfig[];
}

export interface WidgetGridProps {
  widgets: WidgetConfig[];
  globalFilters?: GlobalFilters;
  className?: string;
}

export const WIDGET_SIZES = {
  small: { cols: 3, rows: 3 },
  medium: { cols: 6, rows: 4 },
  large: { cols: 12, rows: 5 },
  wide: { cols: 12, rows: 1 },
} as const;

export type WidgetSize = keyof typeof WIDGET_SIZES;

// oxlint-disable-next-line typescript/consistent-type-assertions
const cssVars = {
  '--widget-cols-small': WIDGET_SIZES.small.cols,
  '--widget-rows-small': WIDGET_SIZES.small.rows,
  '--widget-cols-medium': WIDGET_SIZES.medium.cols,
  '--widget-rows-medium': WIDGET_SIZES.medium.rows,
  '--widget-cols-large': WIDGET_SIZES.large.cols,
  '--widget-rows-large': WIDGET_SIZES.large.rows,
  '--widget-cols-wide': WIDGET_SIZES.wide.cols,
  '--widget-rows-wide': WIDGET_SIZES.wide.rows,
} as CSSProperties;

export function WidgetGrid({ widgets, globalFilters, className }: WidgetGridProps): ReactNode {
  const rows = convertToRows(widgets);

  const getRowHeight = (row: WidgetRow): WidgetSize => {
    if (row.widgets.some(w => w.size === 'wide')) return 'wide';
    // Determine height by tallest widget in the row
    const heights: WidgetSize[] = ['small', 'medium', 'large'];
    const maxHeight = row.widgets.reduce<WidgetSize>((max, widget) => {
      const currentIndex = heights.indexOf(widget.size);
      const maxIndex = heights.indexOf(max);
      return currentIndex > maxIndex ? widget.size : max;
    }, 'small');

    return maxHeight;
  };

  const getSizeClass = (size: WidgetSize): string | undefined => {
    switch (size) {
      case 'small':
        return styles.widgetSmall;
      case 'medium':
        return styles.widgetMedium;
      case 'large':
        return styles.widgetLarge;
      case 'wide':
        return styles.widgetWide;
      default:
        return styles.widgetMedium;
    }
  };

  const getRowHeightClass = (height: WidgetSize): string | undefined => {
    switch (height) {
      case 'small':
        return styles.rowHeightSmall;
      case 'medium':
        return styles.rowHeightMedium;
      case 'large':
        return styles.rowHeightLarge;
      case 'wide':
        return styles.rowHeightWide;
      default:
        return styles.rowHeightMedium;
    }
  };

  return (
    <div className={cx(styles.grid, className)} data-testid="widget-grid" style={cssVars}>
      {rows.map(row => {
        const rowHeight = getRowHeight(row);
        return (
          <div
            className={`${styles.widgetRow} ${getRowHeightClass(rowHeight)}`}
            data-testid={`widget-row-${row.id}`}
            key={row.id}
          >
            {row.widgets.map(widget => {
              const { widgetComponent: WidgetComponent, id, size } = widget;
              return (
                <WidgetComponent
                  globalFilters={globalFilters}
                  key={id}
                  size={size}
                  sizeClass={`${getSizeClass(size)} ${styles.rowWidget}`}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// Helper function to convert flat widget config array to static rows
export function convertToRows(widgets: WidgetConfig[]): WidgetRow[] {
  const rows: WidgetRow[] = [];
  let currentRow: WidgetConfig[] = [];
  let currentRowColumns = 0;

  const getColumnSpan = (size: WidgetSize): number => {
    switch (size) {
      case 'small':
        return WIDGET_SIZES.small.cols;
      case 'medium':
        return WIDGET_SIZES.medium.cols;
      case 'large':
        return WIDGET_SIZES.large.cols;
      case 'wide':
        return WIDGET_SIZES.wide.cols;
      default:
        return WIDGET_SIZES.small.cols;
    }
  };

  widgets
    .filter(widget => !widget.isHidden)
    .forEach(widget => {
      const widgetColumns = getColumnSpan(widget.size);

      // If adding this widget would exceed 12 columns, start a new row
      if (currentRowColumns + widgetColumns > 12) {
        if (currentRow.length > 0) {
          rows.push({
            id: `row-${rows.length + 1}`,
            widgets: [...currentRow],
          });
        }
        currentRow = [widget];
        currentRowColumns = widgetColumns;
      } else {
        currentRow.push(widget);
        currentRowColumns += widgetColumns;
      }
    });

  if (currentRow.length > 0) {
    rows.push({
      id: `row-${rows.length + 1}`,
      widgets: currentRow,
    });
  }

  return rows;
}
